 

import { useState, useRef, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {
  FaPlay,
  FaPause,
  FaStepForward,
  FaStepBackward,
  FaRedo,
  FaInfoCircle,
  FaCode,
  FaBook,
  FaChartBar,
} from "react-icons/fa"
import { MdSpeed, MdOutlineEmojiObjects, MdCompareArrows, MdCalculate } from "react-icons/md"
import { BsEmojiSmile, BsEmojiNeutral, BsEmojiFrown, BsArrowDown } from "react-icons/bs"
import { IoMdCheckmarkCircleOutline, IoMdCloseCircleOutline } from "react-icons/io"
// import Confetti from "react-confetti"
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter"
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism"
import "../styles/rk.css"

const AlgorithmVisualizer = () => {
  // State variables
  const [text, setText] = useState("ABABCABABCABDAB")
  const [pattern, setPattern] = useState("ABCABD")
  const [speed, setSpeed] = useState(1)
  const [isRunning, setIsRunning] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [totalSteps, setTotalSteps] = useState(0)
  const [logs, setLogs] = useState([])
  const [matches, setMatches] = useState([])
  const [activeTab, setActiveTab] = useState("visualization")
  const [showConfetti, setShowConfetti] = useState(false)
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 })
  const [currentExplanation, setCurrentExplanation] = useState("")
  const [highlightedHash, setHighlightedHash] = useState(null)
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipContent, setTooltipContent] = useState("")
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })
  const [difficulty, setDifficulty] = useState("medium")
  const [showCodeExplanation, setShowCodeExplanation] = useState(false)
  const [hashCalculation, setHashCalculation] = useState(null)
  const [showConclusion, setShowConclusion] = useState(false)
  const [conclusionData, setConclusionData] = useState({
    totalComparisons: 0,
    hashMatches: 0,
    falsePositives: 0,
    trueMatches: 0,
    executionTime: 0,
  })
  const [allSteps, setAllSteps] = useState([])

  // Refs
  const animationRef = useRef(null)
  const containerRef = useRef(null)
  const stepTimeoutRef = useRef(null)
  const startTimeRef = useRef(null)

  // Constants
  const prime = 101 // A prime number used for hashing
  const d = 256 // Number of characters in input alphabet

  // Update window size for confetti
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }

    handleResize()
    window.addEventListener("resize", handleResize)

    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Clear animation on unmount
  useEffect(() => {
    return () => {
      if (stepTimeoutRef.current) {
        clearTimeout(stepTimeoutRef.current)
      }
    }
  }, [])

  // Reset visualization
  const resetVisualization = () => {
    if (stepTimeoutRef.current) {
      clearTimeout(stepTimeoutRef.current)
    }
    setIsRunning(false)
    setIsPaused(false)
    setCurrentStep(0)
    setLogs([])
    setMatches([])
    setShowConfetti(false)
    setCurrentExplanation("")
    setHighlightedHash(null)
    setHashCalculation(null)
    setShowConclusion(false)
    setAllSteps([])
    setConclusionData({
      totalComparisons: 0,
      hashMatches: 0,
      falsePositives: 0,
      trueMatches: 0,
      executionTime: 0,
    })
  }

  // Calculate hash value for a string
  const calculateHash = (str, start, end) => {
    let hash = 0

    for (let i = start; i < end; i++) {
      hash = (d * hash + str.charCodeAt(i)) % prime
    }

    return hash
  }

  // Recalculate hash for next window using rolling hash technique
  const recalculateHash = (text, oldIndex, newIndex, oldHash, patternLength) => {
    let newHash = oldHash

    // Remove leading digit
    newHash = (newHash - text.charCodeAt(oldIndex) * Math.pow(d, patternLength - 1)) % prime
    if (newHash < 0) newHash += prime

    // Add trailing digit
    newHash = (newHash * d + text.charCodeAt(newIndex)) % prime

    return newHash
  }

  // Check if the character sequences are actually the same
  const checkEqual = (text, start1, start2, length) => {
    for (let i = 0; i < length; i++) {
      if (text[start1 + i] !== text[start2 + i]) {
        return false
      }
    }
    return true
  }

  // Generate detailed hash calculation steps for display
  const generateHashCalculationSteps = (
    str,
    start,
    end,
    isPattern = false,
    prevHash = null,
    oldIndex = null,
    newIndex = null,
  ) => {
    if (prevHash !== null) {
      // This is a rolling hash calculation
      const patternLength = end - start
      const h = Math.pow(d, patternLength - 1) % prime
      const leadingChar = str.charCodeAt(oldIndex)
      const trailingChar = str.charCodeAt(newIndex)

      const step1 = `Remove leading character: ${prevHash} - (${leadingChar} × ${d}^${patternLength - 1}) % ${prime}`
      const step2 = `= ${prevHash} - ${leadingChar * h} % ${prime}`
      const subtractResult = (prevHash - leadingChar * h) % prime
      const step3 =
        subtractResult < 0 ? `= ${subtractResult} + ${prime} = ${subtractResult + prime}` : `= ${subtractResult}`
      const step4 = `Multiply by ${d}: ${subtractResult < 0 ? subtractResult + prime : subtractResult} × ${d} = ${(subtractResult < 0 ? subtractResult + prime : subtractResult) * d}`
      const step5 = `Add trailing character: ${(subtractResult < 0 ? subtractResult + prime : subtractResult) * d} + ${trailingChar} = ${(subtractResult < 0 ? subtractResult + prime : subtractResult) * d + trailingChar}`
      const finalResult = ((subtractResult < 0 ? subtractResult + prime : subtractResult) * d + trailingChar) % prime
      const step6 = `Final hash: ${(subtractResult < 0 ? subtractResult + prime : subtractResult) * d + trailingChar} % ${prime} = ${finalResult}`

      return {
        title: `Rolling Hash Calculation for "${str.substring(oldIndex + 1, newIndex + 1)}"`,
        steps: [step1, step2, step3, step4, step5, step6],
        result: finalResult,
        formula: `hash(next) = (d × (hash(prev) - text[i] × d^(m-1)) + text[i+m]) % q`,
      }
    } else {
      // This is an initial hash calculation
      const steps = []
      let hash = 0

      steps.push(`Initial hash = 0`)

      for (let i = start; i < end; i++) {
        const char = str[i]
        const ascii = str.charCodeAt(i)
        const prevHash = hash
        hash = (d * hash + ascii) % prime
        steps.push(`hash = (${d} × ${prevHash} + ${ascii}) % ${prime} = ${hash}`)
      }

      return {
        title: isPattern
          ? `Pattern Hash Calculation for "${str.substring(start, end)}"`
          : `Initial Window Hash Calculation for "${str.substring(start, end)}"`,
        steps: steps,
        result: hash,
        formula: `hash(s[0...m-1]) = (s[0] × d^(m-1) + s[1] × d^(m-2) + ... + s[m-1]) % q`,
      }
    }
  }

  // Get explanation for the current step
  const getExplanation = (
    step,
    patternHash,
    textHash,
    currentHash,
    currentWindow,
    position,
    isMatch,
    isFalsePositive,
  ) => {
    if (step === 0) {
      return `We start by calculating the hash value of the pattern "${pattern}" which is ${patternHash}. We also calculate the hash of the first window of text "${text.substring(0, pattern.length)}" which is ${textHash}.`
    } else if (isMatch && !isFalsePositive) {
      return `🎉 Match found! The hash values match (${patternHash} = ${currentHash}) and character-by-character verification confirms that "${currentWindow}" at position ${position} matches our pattern "${pattern}". This is a true positive match.`
    } else if (isMatch && isFalsePositive) {
      return `⚠️ False positive detected! The hash values match (${patternHash} = ${currentHash}) but character-by-character verification shows that "${currentWindow}" at position ${position} is not the same as our pattern "${pattern}". This demonstrates why we need to verify matches even when hash values are equal.`
    } else {
      return `We slide the window to position ${position} and calculate the hash of "${currentWindow}" which is ${currentHash}. This doesn't match our pattern hash (${patternHash}), so we continue. The rolling hash function allows us to efficiently calculate this new hash from the previous window's hash.`
    }
  }

  // Get emoji based on the current state
  const getEmoji = (isMatch, isFalsePositive) => {
    if (isMatch && !isFalsePositive) return <BsEmojiSmile className="emoji happy" />
    if (isMatch && isFalsePositive) return <BsEmojiNeutral className="emoji neutral" />
    return <BsEmojiFrown className="emoji sad" />
  }

  // Run the Rabin-Karp algorithm
  const runRabinKarp = () => {
    resetVisualization()
    setIsRunning(true)
    startTimeRef.current = performance.now()

    const m = pattern.length
    const n = text.length

    if (m > n) {
      setLogs(["Pattern is longer than text. No matches possible."])
      setIsRunning(false)
      return
    }

    const patternHash = calculateHash(pattern, 0, m)
    const textHash = calculateHash(text, 0, m)

    // Generate and display hash calculation steps for pattern
    const patternHashCalc = generateHashCalculationSteps(pattern, 0, m, true)
    setHashCalculation(patternHashCalc)

    const addLog = (message) => {
      setLogs((prevLogs) => [...prevLogs, message])
    }

    addLog(`Calculating pattern hash: Hash("${pattern}") = ${patternHash}`)
    addLog(`Calculating initial text window hash: Hash("${text.substring(0, m)}") = ${textHash}`)

    // Prepare all steps in advance
    const steps = []
    let prevHash = textHash
    let hashMatches = 0
    let falsePositives = 0
    let trueMatches = 0
    let totalComparisons = 0

    for (let i = 0; i <= n - m; i++) {
      const currentWindow = text.substring(i, i + m)
      const currentHash = i === 0 ? textHash : recalculateHash(text, i - 1, i + m - 1, prevHash, m)
      prevHash = currentHash
      totalComparisons++

      const isHashMatch = patternHash === currentHash
      if (isHashMatch) hashMatches++

      const isFullMatch = isHashMatch && checkEqual(text, i, 0, m)
      const isFalsePositive = isHashMatch && !isFullMatch

      if (isFullMatch) trueMatches++
      if (isFalsePositive) falsePositives++

      steps.push({
        position: i,
        currentWindow,
        currentHash,
        isHashMatch,
        isFullMatch,
        isFalsePositive,
        hashCalculation:
          i === 0
            ? generateHashCalculationSteps(text, 0, m, false)
            : generateHashCalculationSteps(text, i - 1, i + m, false, prevHash, i - 1, i + m - 1),
        explanation: getExplanation(
          i,
          patternHash,
          textHash,
          currentHash,
          currentWindow,
          i,
          isHashMatch,
          isFalsePositive,
        ),
      })
    }

    setAllSteps(steps)
    setTotalSteps(steps.length)

    // Function to execute a single step
    const executeStep = (stepIndex) => {
      if (stepIndex >= steps.length) {
        const endTime = performance.now()
        const executionTime = ((endTime - startTimeRef.current) / 1000).toFixed(2)

        setConclusionData({
          totalComparisons,
          hashMatches,
          falsePositives,
          trueMatches,
          executionTime,
        })

        setShowConclusion(true)
        setIsRunning(false)

        if (trueMatches > 0) {
          setShowConfetti(true)
          setTimeout(() => setShowConfetti(false), 5000)
        }
        return
      }

      const step = steps[stepIndex]
      setCurrentStep(stepIndex)

      // Set the hash calculation for this step
      setHashCalculation(step.hashCalculation)

      addLog(`Window ${stepIndex + 1}: "${step.currentWindow}" (Position ${step.position}), Hash = ${step.currentHash}`)

      setHighlightedHash({
        type: "text",
        hash: step.currentHash,
        position: step.position,
      })

      setCurrentExplanation(step.explanation)

      if (step.isFullMatch) {
        setMatches((prev) => [...prev, step.position])
      }

      // Schedule next step if not paused
      if (!isPaused) {
        const timeoutDuration = 2000 / speed
        stepTimeoutRef.current = setTimeout(() => {
          executeStep(stepIndex + 1)
        }, timeoutDuration)
      }
    }

    // Start execution with the first step
    executeStep(0)
  }

  // Pause or resume the animation
  const togglePause = () => {
    if (isPaused) {
      setIsPaused(false)
      // Resume from current step
      const timeoutDuration = 2000 / speed
      stepTimeoutRef.current = setTimeout(() => {
        executeStepByIndex(currentStep + 1)
      }, timeoutDuration)
    } else {
      setIsPaused(true)
      if (stepTimeoutRef.current) {
        clearTimeout(stepTimeoutRef.current)
      }
    }
  }

  // Execute a specific step by index
  const executeStepByIndex = (stepIndex) => {
    if (stepIndex < 0 || stepIndex >= allSteps.length) return

    if (stepTimeoutRef.current) {
      clearTimeout(stepTimeoutRef.current)
    }

    const step = allSteps[stepIndex]
    setCurrentStep(stepIndex)
    setHashCalculation(step.hashCalculation)

    setHighlightedHash({
      type: "text",
      hash: step.currentHash,
      position: step.position,
    })

    setCurrentExplanation(step.explanation)

    // If this is a new match, add it
    if (step.isFullMatch && !matches.includes(step.position)) {
      setMatches((prev) => [...prev, step.position])
    }

    // Schedule next step if not paused and not at the end
    if (!isPaused && stepIndex < allSteps.length - 1) {
      const timeoutDuration = 2000 / speed
      stepTimeoutRef.current = setTimeout(() => {
        executeStepByIndex(stepIndex + 1)
      }, timeoutDuration)
    } else if (stepIndex >= allSteps.length - 1) {
      // We've reached the end
      const endTime = performance.now()
      const executionTime = ((endTime - startTimeRef.current) / 1000).toFixed(2)

      setConclusionData({
        totalComparisons: allSteps.length,
        hashMatches: allSteps.filter((s) => s.isHashMatch).length,
        falsePositives: allSteps.filter((s) => s.isFalsePositive).length,
        trueMatches: allSteps.filter((s) => s.isFullMatch).length,
        executionTime,
      })

      setShowConclusion(true)
      setIsRunning(false)

      if (allSteps.some((s) => s.isFullMatch)) {
        setShowConfetti(true)
        setTimeout(() => setShowConfetti(false), 5000)
      }
    }
  }

  // Step forward manually
  const stepForward = () => {
    if (currentStep < totalSteps - 1) {
      executeStepByIndex(currentStep + 1)
    }
  }

  // Step backward manually
  const stepBackward = () => {
    if (currentStep > 0) {
      executeStepByIndex(currentStep - 1)
    }
  }

  // Handle tooltip display
  const showHashTooltip = (hash, event) => {
    const explanation = `This hash value (${hash}) is calculated using a polynomial rolling hash function: (s[0]*256^(m-1) + s[1]*256^(m-2) + ... + s[m-1]) mod 101, where s[i] is the ASCII value of each character.`

    setTooltipContent(explanation)
    setTooltipPosition({ x: event.clientX, y: event.clientY })
    setShowTooltip(true)
  }

  const hideTooltip = () => {
    setShowTooltip(false)
  }

  // Display the text with highlighting
  const displayText = () => {
    return Array.from(text).map((char, index) => {
      const isHighlighted =
        matches.includes(index) || matches.some((matchPos) => index >= matchPos && index < matchPos + pattern.length)

      const isCurrentWindow =
        currentStep < totalSteps &&
        index >= allSteps[currentStep]?.position &&
        index < allSteps[currentStep]?.position + pattern.length

      const isHashMatch =
        currentStep < totalSteps &&
        allSteps[currentStep]?.isHashMatch &&
        index >= allSteps[currentStep]?.position &&
        index < allSteps[currentStep]?.position + pattern.length

      const isFalsePositive =
        currentStep < totalSteps &&
        allSteps[currentStep]?.isFalsePositive &&
        index >= allSteps[currentStep]?.position &&
        index < allSteps[currentStep]?.position + pattern.length

      let className = "text-char"
      if (isHighlighted) className += " matched"
      if (isCurrentWindow) className += " current-window"
      if (isHashMatch && !isFalsePositive) className += " hash-match"
      if (isFalsePositive) className += " false-positive"

      return (
        <motion.div
          key={index}
          className={className}
          initial={{ scale: 1 }}
          animate={
            isHighlighted
              ? {
                  scale: [1, 1.2, 1],
                  boxShadow: [
                    "0px 0px 0px rgba(255, 87, 34, 0)",
                    "0px 0px 10px rgba(255, 87, 34, 0.7)",
                    "0px 0px 0px rgba(255, 87, 34, 0)",
                  ],
                }
              : {}
          }
          transition={{ duration: 1, repeat: isHighlighted ? Number.POSITIVE_INFINITY : 0, repeatDelay: 1 }}
        >
          {char}
        </motion.div>
      )
    })
  }

  // Display pattern with comparison to current window
  const displayPatternComparison = () => {
    if (currentStep >= totalSteps || !allSteps[currentStep]) return null

    const currentWindowStart = allSteps[currentStep]?.position || 0
    const currentWindow = text.substring(currentWindowStart, currentWindowStart + pattern.length)

    return (
      <div className="pattern-comparison">
        <div className="comparison-header">
          <h4>Pattern Comparison</h4>
          <div className="comparison-status">
            {allSteps[currentStep]?.isHashMatch ? (
              allSteps[currentStep]?.isFullMatch ? (
                <span className="match-status success">
                  <IoMdCheckmarkCircleOutline /> Match
                </span>
              ) : (
                <span className="match-status warning">
                  <IoMdCloseCircleOutline /> False Positive
                </span>
              )
            ) : (
              <span className="match-status neutral">
                <MdCompareArrows /> Comparing
              </span>
            )}
          </div>
        </div>

        <div className="comparison-content">
          <div className="comparison-row">
            <div className="comparison-label">Pattern:</div>
            <div className="comparison-chars">
              {Array.from(pattern).map((char, index) => {
                const isMatch = currentWindow[index] === char
                return (
                  <div
                    key={`pattern-${index}`}
                    className={`comparison-char ${allSteps[currentStep]?.isHashMatch ? (isMatch ? "match" : "mismatch") : ""}`}
                  >
                    {char}
                  </div>
                )
              })}
            </div>
          </div>

          <div className="comparison-arrows">
            {Array.from(pattern).map((_, index) => (
              <div key={`arrow-${index}`} className="comparison-arrow">
                {currentWindow[index] === pattern[index] ? (
                  <BsArrowDown className="arrow-match" />
                ) : (
                  <BsArrowDown className="arrow-mismatch" />
                )}
              </div>
            ))}
          </div>

          <div className="comparison-row">
            <div className="comparison-label">Window:</div>
            <div className="comparison-chars">
              {Array.from(currentWindow).map((char, index) => {
                const isMatch = pattern[index] === char
                return (
                  <div
                    key={`window-${index}`}
                    className={`comparison-char ${allSteps[currentStep]?.isHashMatch ? (isMatch ? "match" : "mismatch") : ""}`}
                  >
                    {char}
                  </div>
                )
              })}
            </div>
            <div className="window-position">Position: {allSteps[currentStep]?.position}</div>
          </div>
        </div>
      </div>
    )
  }

  // Display hash calculation steps
  const displayHashCalculation = () => {
    if (!hashCalculation) return null

    return (
      <div className="hash-calculation">
        <div className="hash-calculation-header">
          <h4>
            <MdCalculate /> {hashCalculation.title}
          </h4>
        </div>
        <div className="hash-formula">
          <span className="formula-label">Formula:</span> {hashCalculation.formula}
        </div>
        <div className="hash-steps">
          {hashCalculation.steps.map((step, index) => (
            <motion.div
              key={index}
              className="hash-step"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <span className="step-number">{index + 1}.</span> {step}
            </motion.div>
          ))}
        </div>
        <div className="hash-result">
          <span className="result-label">Final Hash:</span> {hashCalculation.result}
        </div>
      </div>
    )
  }

  // Display algorithm conclusion
  const displayConclusion = () => {
    if (!showConclusion) return null

    return (
      <motion.div
        className="algorithm-conclusion"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h3>Algorithm Execution Summary</h3>
        <div className="conclusion-content">
          <div className="conclusion-stats">
            <div className="rkstat-item">
              <div className="rkstat-value">{conclusionData.totalComparisons}</div>
              <div className="rkstat-label">Total Windows Compared</div>
            </div>
            <div className="rkstat-item">
              <div className="rkstat-value">{conclusionData.hashMatches}</div>
              <div className="rkstat-label">Hash Matches</div>
            </div>
            <div className="rkstat-item">
              <div className="rkstat-value">{conclusionData.falsePositives}</div>
              <div className="rkstat-label">False Positives</div>
            </div>
            <div className="rkstat-item">
              <div className="rkstat-value">{conclusionData.trueMatches}</div>
              <div className="rkstat-label">True Matches</div>
            </div>
            <div className="rkstat-item">
              <div className="rkstat-value">{conclusionData.executionTime}s</div>
              <div className="rkstat-label">Execution Time</div>
            </div>
          </div>

          <div className="conclusion-analysis">
            <h4>Analysis</h4>
            <p>
              The Rabin-Karp algorithm found {conclusionData.trueMatches} match
              {conclusionData.trueMatches !== 1 ? "es" : ""} in the text. Out of {conclusionData.totalComparisons}{" "}
              window comparisons, there were {conclusionData.hashMatches} hash matches, of which{" "}
              {conclusionData.falsePositives} were false positives.
            </p>
            <p>
              {conclusionData.falsePositives > 0
                ? "The presence of false positives demonstrates why character-by-character verification is necessary even when hash values match."
                : "In this example, there were no false positives, but in general, hash collisions can occur, which is why character verification is always needed."}
            </p>
            <p>
              The efficiency of Rabin-Karp comes from its ability to quickly skip non-matching windows using hash
              comparisons, only performing character-by-character comparison when hash values match.
            </p>
          </div>
        </div>
      </motion.div>
    )
  }

  // Algorithm code with syntax highlighting
  const algorithmCode = `function rabinKarp(text, pattern) {
  const d = 256; // Number of characters in input alphabet
  const q = 101; // A prime number
  const m = pattern.length;
  const n = text.length;
  let p = 0; // Hash value for pattern
  let t = 0; // Hash value for text
  let h = 1;
  const matches = [];

  // Calculate h = d^(m-1) % q
  for (let i = 0; i < m - 1; i++) {
    h = (h * d) % q;
  }

  // Calculate hash value for pattern and first window of text
  for (let i = 0; i < m; i++) {
    p = (d * p + pattern.charCodeAt(i)) % q;
    t = (d * t + text.charCodeAt(i)) % q;
  }

  // Slide the pattern over text one by one
  for (let i = 0; i <= n - m; i++) {
    // Check if hash values match
    if (p === t) {
      // Check for characters one by one
      let j;
      for (j = 0; j < m; j++) {
        if (text[i + j] !== pattern[j]) break;
      }
      
      // If all characters match, we found a pattern
      if (j === m) {
        matches.push(i);
      }
    }

    // Calculate hash value for next window
    if (i < n - m) {
      t = (d * (t - text.charCodeAt(i) * h) + text.charCodeAt(i + m)) % q;
      if (t < 0) t += q;
    }
  }

  return matches;
}`

  // Code explanation sections
  const codeExplanations = [
    {
      title: "Hash Calculation",
      content:
        "The hash function converts a string into a number using a polynomial rolling hash. Each character contributes to the hash based on its ASCII value and position in the string.",
    },
    {
      title: "Rolling Hash Technique",
      content:
        "Instead of recalculating the entire hash for each window, we efficiently compute the next hash using the previous one. This is what makes Rabin-Karp efficient.",
    },
    {
      title: "Character Verification",
      content:
        "When hash values match, we still need to verify character by character because different strings can have the same hash value (hash collision).",
    },
  ]

  return (
    <div className="rkalgorithm-visualizer" ref={containerRef}>
      {/* {showConfetti && (
        <Confetti width={windowSize.width} height={windowSize.height} recycle={false} numberOfPieces={200} />
      )} */}

      <header className="visualizer-header">
        <div className="rkalgorithm-title">
          <h2>Rabin-Karp String Matching Algorithm</h2>
        </div>
      </header>

      <div className="visualizer-content">
        <nav className="visualizer-nav">
          <button
            className={`nav-button ${activeTab === "visualization" ? "active" : ""}`}
            onClick={() => setActiveTab("visualization")}
          >
            <FaPlay /> Visualization
          </button>
          <button
            className={`nav-button ${activeTab === "algorithm" ? "active" : ""}`}
            onClick={() => setActiveTab("algorithm")}
          >
            <FaBook /> Algorithm
          </button>
          <button className={`nav-button ${activeTab === "code" ? "active" : ""}`} onClick={() => setActiveTab("code")}>
            <FaCode /> Code
          </button>
          <button
            className={`nav-button ${activeTab === "complexity" ? "active" : ""}`}
            onClick={() => setActiveTab("complexity")}
          >
            <FaChartBar /> Complexity
          </button>
        </nav>

        <main className="visualizer-main">
          <AnimatePresence mode="wait">
            {activeTab === "visualization" && (
              <motion.div
                key="visualization"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="visualization-tab"
              >
                <div className="input-section">
                  <div className="input-group">
                    <label htmlFor="text-input">
                      <span>Text:</span>
                      <FaInfoCircle className="info-icon" title="Enter the text to search within" />
                    </label>
                    <input
                      id="text-input"
                      type="text"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      disabled={isRunning}
                      placeholder="Enter text to search in"
                    />
                  </div>

                  <div className="input-group">
                    <label htmlFor="pattern-input">
                      <span>Pattern:</span>
                      <FaInfoCircle className="info-icon" title="Enter the pattern to search for" />
                    </label>
                    <input
                      id="pattern-input"
                      type="text"
                      value={pattern}
                      onChange={(e) => setPattern(e.target.value)}
                      disabled={isRunning}
                      placeholder="Enter pattern to search for"
                    />
                  </div>

                  <div className="input-group speed-group">
                    <label htmlFor="speed-input">
                      <MdSpeed className="speed-icon" />
                      <span>Speed:</span>
                    </label>
                    <div className="speed-control">
                      <input
                        id="speed-input"
                        type="range"
                        min="0.5"
                        max="3"
                        step="0.5"
                        value={speed}
                        onChange={(e) => setSpeed(Number.parseFloat(e.target.value))}
                        disabled={isRunning && !isPaused}
                      />
                      <span className="speed-value">{speed}x</span>
                    </div>
                  </div>
                </div>

                <div className="controls-section">
                  <button
                    className="control-button play-button"
                    onClick={runRabinKarp}
                    disabled={isRunning && !isPaused}
                  >
                    <FaPlay />
                    <span>Start</span>
                  </button>

                  <button className="control-button pause-button" onClick={togglePause} disabled={!isRunning}>
                    <FaPause />
                    <span>{isPaused ? "Resume" : "Pause"}</span>
                  </button>

                  <button
                    className="control-button step-button"
                    onClick={stepBackward}
                    disabled={!isPaused || currentStep <= 0}
                  >
                    <FaStepBackward />
                    <span>Previous</span>
                  </button>

                  <button
                    className="control-button step-button"
                    onClick={stepForward}
                    disabled={!isPaused || currentStep >= totalSteps - 1}
                  >
                    <FaStepForward />
                    <span>Next</span>
                  </button>

                  <button
                    className="control-button reset-button"
                    onClick={resetVisualization}
                    disabled={!isRunning && currentStep === 0}
                  >
                    <FaRedo />
                    <span>Reset</span>
                  </button>
                </div>

                <div className="visualization-section">
                  <h3>Text Visualization</h3>
                  <div className="text-display">
                    {displayText()}
                    {highlightedHash && (
                      <div
                        className="hash-display"
                        style={{
                          left: `${highlightedHash.position * 50 + 25}px`,
                          top: "-40px",
                        }}
                        onMouseEnter={(e) => showHashTooltip(highlightedHash.hash, e)}
                        onMouseLeave={hideTooltip}
                      >
                        Hash: {highlightedHash.hash}
                      </div>
                    )}
                  </div>

                  {showTooltip && (
                    <div
                      className="tooltip"
                      style={{
                        left: `${tooltipPosition.x}px`,
                        top: `${tooltipPosition.y - 100}px`,
                      }}
                    >
                      {tooltipContent}
                    </div>
                  )}

                  <div className="visualization-flex-container">
                    <div className="visualization-main-column">
                      {/* Pattern comparison section */}
                      {displayPatternComparison()}

                      <div className="explanation-box">
                        <div className="explanation-header">
                          <h3>Current Step Explanation</h3>
                          {currentStep < totalSteps &&
                            allSteps[currentStep]?.isHashMatch &&
                            getEmoji(allSteps[currentStep]?.isFullMatch, allSteps[currentStep]?.isFalsePositive)}
                        </div>
                        <div className="explanation-content">
                          {currentExplanation || "Click 'Start' to begin the visualization."}
                        </div>
                      </div>
                    </div>

                    <div className="visualization-side-column">
                      {/* Hash calculation steps */}
                      {displayHashCalculation()}
                    </div>
                  </div>
                </div>

                {/* Algorithm conclusion */}
                {displayConclusion()}

                <div className="results-section">
                  {matches.length > 0 && (
                    <div className="matches-display">
                      <h3>Matches Found: {matches.length}</h3>
                      <ul className="matches-list">
                        {matches.map((pos, idx) => (
                          <motion.li
                            key={idx}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.1 }}
                            className="match-item"
                          >
                            <span className="match-position">Position {pos}:</span>
                            <span className="match-text">"{text.substring(pos, pos + pattern.length)}"</span>
                          </motion.li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="logs-display">
                    <h3>Algorithm Steps</h3>
                    <div className="logs-content">
                      {logs.length === 0 ? (
                        <div className="empty-logs">Click 'Start' to see the algorithm in action</div>
                      ) : (
                        logs.map((log, index) => (
                          <motion.div
                            key={index}
                            className="log-entry"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                          >
                            <span className="log-number">{index + 1}.</span>
                            <span className="log-message">{log}</span>
                          </motion.div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === "algorithm" && (
              <motion.div
                key="algorithm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="algorithm-tab"
              >
                <div className="algorithm-content">
                  <h3>How Rabin-Karp Algorithm Works</h3>

                  <div className="algorithm-section">
                    <h4>Overview</h4>
                    <p>
                      The Rabin-Karp algorithm is a string-searching algorithm that uses hashing to find patterns in
                      text. Instead of checking each character of the pattern at each position of the text, it computes
                      a hash value for the pattern and for each substring of the text with the same length as the
                      pattern.
                    </p>
                  </div>

                  <div className="algorithm-section">
                    <h4>Key Steps</h4>
                    <ol className="steps-list">
                      <motion.li
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 }}
                      >
                        <strong>Preprocessing:</strong> Calculate a hash value for the pattern.
                      </motion.li>
                      <motion.li
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 }}
                      >
                        <strong>Initial Hash:</strong> Calculate the hash value for the first window of the text (same
                        length as pattern).
                      </motion.li>
                      <motion.li
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}
                      >
                        <strong>Hash Comparison:</strong> Compare the hash values of the pattern and text window.
                      </motion.li>
                      <motion.li
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 }}
                      >
                        <strong>Character Check:</strong> If hash values match, verify character by character.
                      </motion.li>
                      <motion.li
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5 }}
                      >
                        <strong>Rolling Hash:</strong> Efficiently calculate the hash for the next text window using the
                        previous hash.
                      </motion.li>
                      <motion.li
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.6 }}
                      >
                        <strong>Slide and Repeat:</strong> Slide the window one position at a time and repeat steps 3-5.
                      </motion.li>
                    </ol>
                  </div>

                  <div className="algorithm-section">
                    <h4>Hash Function</h4>
                    <div className="hash-explanation">
                      <p>The hash function used in Rabin-Karp is a polynomial rolling hash:</p>
                      <div className="math-formula">
                        hash(s[0...m-1]) = (s[0] * 256<sup>m-1</sup> + s[1] * 256<sup>m-2</sup> + ... + s[m-1]) mod 101
                      </div>
                      <p>Where:</p>
                      <ul>
                        <li>
                          <strong>s[i]</strong> is the ASCII value of the character at position i
                        </li>
                        <li>
                          <strong>256</strong> is the number of characters in the input alphabet
                        </li>
                        <li>
                          <strong>101</strong> is a prime number used to keep the hash value manageable
                        </li>
                        <li>
                          <strong>m</strong> is the length of the pattern
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="algorithm-section">
                    <h4>Rolling Hash Technique</h4>
                    <p>The key efficiency of Rabin-Karp comes from the rolling hash calculation:</p>
                    <div className="math-formula">
                      hash(s[i+1...i+m]) = (256 * (hash(s[i...i+m-1]) - s[i] * 256<sup>m-1</sup>) + s[i+m]) mod 101
                    </div>
                    <p>
                      This allows us to compute the hash of the next window in O(1) time using the hash of the current
                      window.
                    </p>
                  </div>

                  <div className="algorithm-section">
                    <h4>Applications</h4>
                    <ul className="applications-list">
                      <li>Plagiarism detection systems</li>
                      <li>DNA sequence matching in bioinformatics</li>
                      <li>Detecting duplicate files through hash fingerprinting</li>
                      <li>Multiple pattern searching</li>
                      <li>Intrusion detection systems</li>
                    </ul>
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === "code" && (
              <motion.div
                key="code"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="code-tab"
              >
                <div className="code-content">
                  <div className="code-header">
                    <h3>Rabin-Karp Implementation</h3>
                    <button className="explanation-toggle" onClick={() => setShowCodeExplanation(!showCodeExplanation)}>
                      {showCodeExplanation ? "Hide Explanation" : "Show Explanation"}
                    </button>
                  </div>

                  <div className="code-container">
                    <SyntaxHighlighter
                      language="javascript"
                      style={vscDarkPlus}
                      showLineNumbers={true}
                      wrapLines={true}
                    >
                      {algorithmCode}
                    </SyntaxHighlighter>
                  </div>

                  {showCodeExplanation && (
                    <div className="code-explanation">
                      {codeExplanations.map((section, index) => (
                        <motion.div
                          key={index}
                          className="explanation-section"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <h4>{section.title}</h4>
                          <p>{section.content}</p>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {activeTab === "complexity" && (
              <motion.div
                key="complexity"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="complexity-tab"
              >
                <div className="complexity-content">
                  <h3>Time and Space Complexity</h3>

                  <div className="complexity-grid">
                    <div className="complexity-card best-case">
                      <h4>Best Case</h4>
                      <div className="complexity-value">O(n + m)</div>
                      <p>
                        Where n is the length of the text and m is the length of the pattern. This occurs when there are
                        few or no hash collisions.
                      </p>
                    </div>

                    <div className="complexity-card average-case">
                      <h4>Average Case</h4>
                      <div className="complexity-value">O(n + m)</div>
                      <p>
                        With a good hash function, the average case is also O(n + m). The algorithm spends O(m) time to
                        compute the hash value of the pattern and O(n) time to compute and compare hash values for all
                        positions.
                      </p>
                    </div>

                    <div className="complexity-card worst-case">
                      <h4>Worst Case</h4>
                      <div className="complexity-value">O(n × m)</div>
                      <p>
                        This occurs when all hash values match, requiring character-by-character comparison at every
                        position, but the pattern doesn't actually match anywhere.
                      </p>
                    </div>

                    <div className="complexity-card space-complexity">
                      <h4>Space Complexity</h4>
                      <div className="complexity-value">O(1)</div>
                      <p>
                        Rabin-Karp uses a constant amount of extra space regardless of input size. This makes it
                        memory-efficient for large texts.
                      </p>
                    </div>
                  </div>

                  <div className="comparison-section">
                    <h4>Comparison with Other String Matching Algorithms</h4>
                    <div className="comparison-table-container">
                      <table className="comparison-table">
                        <thead>
                          <tr>
                            <th>Algorithm</th>
                            <th>Preprocessing</th>
                            <th>Matching</th>
                            <th>Space</th>
                            <th>Best For</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>Naive</td>
                            <td>None</td>
                            <td>O(n × m)</td>
                            <td>O(1)</td>
                            <td>Short patterns, simple cases</td>
                          </tr>
                          <tr className="highlighted-row">
                            <td>Rabin-Karp</td>
                            <td>O(m)</td>
                            <td>O(n + m) to O(n × m)</td>
                            <td>O(1)</td>
                            <td>Multiple pattern search</td>
                          </tr>
                          <tr>
                            <td>KMP</td>
                            <td>O(m)</td>
                            <td>O(n)</td>
                            <td>O(m)</td>
                            <td>Single pattern search</td>
                          </tr>
                          <tr>
                            <td>Boyer-Moore</td>
                            <td>O(m + σ)</td>
                            <td>O(n) to O(n × m)</td>
                            <td>O(m + σ)</td>
                            <td>Large alphabets</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p className="table-note">σ represents the size of the alphabet</p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>

      <footer className="visualizer-footer">
        <p>© 2025 AlgoViz - Interactive Algorithm Visualization Platform</p>
      </footer>
    </div>
  )
}

export default AlgorithmVisualizer
